import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Newspaper, FileText, Images, ListChecks, ArrowRight, X,
} from "lucide-react";
import { istNeu } from "@/components/portal/ZuordnungPicker";

// ---------------------------------------------------------------------------
// Persistenz: dismissed IDs mit Ablaufzeit (max 30 Tage)
// ---------------------------------------------------------------------------
const LS_KEY = "portal_dismissed_v1";
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const readDismissed = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return {};
    const now = Date.now();
    let changed = false;
    const cleaned = {};
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "number" && v > now) cleaned[k] = v;
      else changed = true;
    }
    if (changed) localStorage.setItem(LS_KEY, JSON.stringify(cleaned));
    return cleaned;
  } catch { return {}; }
};

const writeDismissed = (map) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
};

const dismissKey = (typ, id) => `${typ}:${id}`;

// ---------------------------------------------------------------------------
// Umfragen-Banner (prominent, NICHT wegklickbar)
// ---------------------------------------------------------------------------
export const OffeneUmfragenBanner = () => {
  const [offen, setOffen] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const { data } = await api.get("/surveys");
        if (abort) return;
        const heute = new Date().toISOString().slice(0, 10);
        const off = (data || []).filter(
          (s) => !s.bereits_beantwortet && (!s.deadline || s.deadline >= heute),
        );
        setOffen(off);
      } catch { /* silent */ }
      finally { if (!abort) setReady(true); }
    })();
    return () => { abort = true; };
  }, []);

  if (!ready || offen.length === 0) return null;
  const einzeln = offen.length === 1;
  const ziel = einzeln ? `/portal/umfrage/${offen[0].id}` : "/portal";

  return (
    <div
      data-testid="portal-offene-umfragen-banner"
      className="rounded-xl border border-primary/40 bg-primary/[0.06] p-4"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-primary text-primary-foreground">
          <ListChecks className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium text-primary">
            {einzeln
              ? "Eine Umfrage wartet auf Ihre Rückmeldung"
              : `${offen.length} Umfragen warten auf Ihre Rückmeldung`}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {einzeln
              ? offen[0].titel
              : offen.slice(0, 3).map((s) => s.titel).join(" · ")
                + (offen.length > 3 ? ` +${offen.length - 3} weitere` : "")}
          </p>
        </div>
        <Button asChild size="sm" data-testid="portal-umfragen-cta">
          <Link to={ziel}>
            {einzeln ? "Jetzt beantworten" : "Zum Portal"}
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Link>
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Neuigkeiten-Banner (Dokumente + Galerien) – je Objekt wegklickbar
// ---------------------------------------------------------------------------
export const NeueInhalteBanner = () => {
  const [dokumente, setDokumente] = useState([]);
  const [galerien, setGalerien] = useState([]);
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const [d, g] = await Promise.all([
          api.get("/documents").then((r) => r.data).catch(() => []),
          api.get("/galleries").then((r) => r.data).catch(() => []),
        ]);
        if (abort) return;
        setDokumente((d || []).filter((x) => istNeu(x.erstellt_am, x.neu_badge_tage)));
        setGalerien((g || []).filter((x) => istNeu(x.erstellt_am, x.neu_badge_tage)));
      } catch { /* silent */ }
      finally { if (!abort) setReady(true); }
    })();
    return () => { abort = true; };
  }, []);

  const eintraege = useMemo(() => {
    const list = [
      ...dokumente.map((d) => ({
        typ: "dokument", id: d.id, titel: d.titel,
        link: `/portal/dokument/${d.id}`,
        icon: FileText, meta: d.dateiname,
        erstellt_am: d.erstellt_am,
      })),
      ...galerien.map((g) => ({
        typ: "galerie", id: g.id, titel: g.titel,
        link: `/portal/galerie/${g.id}`,
        icon: Images, meta: `${g.foto_count || 0} Foto(s)`,
        erstellt_am: g.erstellt_am,
      })),
    ];
    // Sortiere neueste zuerst, dann filter dismissed
    list.sort((a, b) => (b.erstellt_am || "").localeCompare(a.erstellt_am || ""));
    return list.filter((e) => !dismissed[dismissKey(e.typ, e.id)]);
  }, [dokumente, galerien, dismissed]);

  const dismiss = (typ, id) => {
    const next = {
      ...dismissed,
      [dismissKey(typ, id)]: Date.now() + DEFAULT_TTL_MS,
    };
    setDismissed(next);
    writeDismissed(next);
  };

  if (!ready || eintraege.length === 0) return null;

  const sichtbar = eintraege.slice(0, 5);
  const rest = eintraege.length - sichtbar.length;

  return (
    <div
      data-testid="portal-neuigkeiten-banner"
      className="rounded-xl border bg-secondary/40 p-3 sm:p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <Newspaper className="h-4 w-4 text-primary" />
        <p className="text-sm font-medium">Neu im Portal</p>
        <span className="text-xs text-muted-foreground">
          ({eintraege.length} {eintraege.length === 1 ? "Eintrag" : "Einträge"})
        </span>
      </div>
      <div className="space-y-1.5">
        {sichtbar.map((e) => {
          const Icon = e.icon;
          return (
            <div
              key={dismissKey(e.typ, e.id)}
              data-testid={`neu-eintrag-${e.typ}-${e.id}`}
              className="group flex items-center gap-2 rounded-md border bg-card p-2 pl-3 hover:bg-primary/5 transition-colors"
            >
              <Icon className="h-4 w-4 text-primary shrink-0" />
              <Link
                to={e.link}
                className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5"
              >
                <span className="text-sm font-medium truncate max-w-[70%]">{e.titel}</span>
                <span className="text-[11px] text-muted-foreground truncate">
                  {e.typ === "dokument" ? "Dokument" : "Galerie"} · {e.meta}
                </span>
              </Link>
              <button
                type="button"
                onClick={() => dismiss(e.typ, e.id)}
                data-testid={`neu-dismiss-${e.typ}-${e.id}`}
                aria-label="Ausblenden"
                title="Für 30 Tage ausblenden"
                className="h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
        {rest > 0 && (
          <Link
            to="/portal"
            className="block text-center text-xs text-primary hover:underline pt-1"
            data-testid="neu-mehr-link"
          >
            +{rest} weitere im Portal ansehen
          </Link>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Kombinierter Banner-Block für's Dashboard
// Nur für Rolle "lieferant" sichtbar. Für admin/sysadmin/berater ausgeblendet,
// da diese Rollen nicht als Umfrage-/Dokument-Empfänger addressierbar sind.
// ---------------------------------------------------------------------------
export const PortalHinweisBanner = () => {
  const { user } = useAuth();
  if (user?.rolle !== "lieferant") return null;
  return (
    <>
      <OffeneUmfragenBanner />
      <NeueInhalteBanner />
    </>
  );
};

// ---------------------------------------------------------------------------
// Badge für Menüeintrag (unverändert)
// ---------------------------------------------------------------------------
export const PortalOffeneUmfragenBadge = ({ className = "" }) => {
  const [offen, setOffen] = useState(0);

  useEffect(() => {
    let abort = false;
    const load = async () => {
      try {
        const { data } = await api.get("/surveys");
        if (abort) return;
        const heute = new Date().toISOString().slice(0, 10);
        const off = (data || []).filter(
          (s) => !s.bereits_beantwortet && (!s.deadline || s.deadline >= heute),
        );
        setOffen(off.length);
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { abort = true; clearInterval(iv); };
  }, []);

  if (offen <= 0) return null;
  return (
    <span
      data-testid="portal-badge"
      className={`ml-auto inline-flex min-w-[18px] items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1.5 py-0.5 ${className}`}
    >
      {offen > 99 ? "99+" : offen}
    </span>
  );
};
